import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useRequestSections } from '../../../api/request';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/Store';
import axios from 'axios';
import { decodeUnzip } from '../../Decryption/Decryption';
import { updateLanguagePack } from '../../../redux/reducer/languageReducer';
import { PersistHelper } from '../../../redux/persistHelper';
import customNotifications from './customNotifications';
import { contextSupportTasksBids } from '../../../constants';

const useGetScriptHash = () => {
  // const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [isErrorScript, setIsErrorScript] = useState(false);
  const [isTaskScriptSettled, setIsTaskScriptSettled] = useState(false);

  const { Script } = useRequestSections();
  const contextId = useSelector(
    (state: RootState) => state.contextId.contextId
  );

  const contextType = useSelector(
    (state: RootState) => state.contextId.queryObj?.context
  );
  // Contexts that show the Tasks/Bids tabs also need the task library loaded.
  const supportsTasksBids = contextSupportTasksBids[contextType] ?? false;

  // Fetch script hash using the query
  const {
    data: scriptHashData,
    isLoading: isScriptHashDataLoading,
    isFetching: isScriptHashDataFetching,
    refetch,
    error: scriptHashDataError,
  } = useQuery({
    queryKey: ['scriptHash', contextId],
    queryFn: async () => {
      const isPersisted = await PersistHelper.isDataPersisted(contextId);

      if (!isPersisted) {
        try {
          const response = await Script?.getScriptHash(contextId);
          // GetScriptHashResponse: { hash, hashes }. Return the whole payload so
          // callers get the primary `hash` plus the `hashes` map (e.g.
          // hashes.TaskLibrary for the task-library script).
          return response?.data;
        } catch (error: any) {
          const errorCode =
            error?.response?.status || error?.code || 'Unknown Error';
          customNotifications('Script hash', errorCode);
          setIsErrorScript(true);
          //PersistHelper?.clearAllData();
        }
      }
      return null;
    },
    enabled: !!contextId,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  // Mutation for fetching script details using the script hash
  const {
    mutateAsync: getScript,
    isPending: isGetScriptPending,
    error: getScriptError,
    isSuccess: successfulGetScript,
    data: getScriptData,
  } = useMutation({
    mutationFn: async (scriptHash: string) => {
      const response = await Script?.getScript(scriptHash);
      return {
        scriptHash: response?.data?.scriptHash,
        scriptValueGzip: response?.data?.scriptValueGzip,
        labelsGzip: response?.data?.labelsGzip,
      };
    },
    onError: (error: any) => {
      const errorCode =
        error?.response?.status || error?.code || 'Unknown Error';
      customNotifications('Script data', errorCode);
      setIsErrorScript(true);
      //PersistHelper?.clearAllData();
    },
  });

  const {
    mutateAsync: getScriptLanguagePack,
    isPending: isGetScriptLanguagePackPending,
    error: getScriptLanguagePackError,
    isSuccess: successfulGetScriptLanguagePack,
    data: getScriptLanguagePackData,
  } = useMutation({
    mutationFn: async (scriptHash: string) => {
      const response = await Script?.getScriptLanguagePack(scriptHash);
      if (response && response?.data?.url) {
        // Step 2: Fetch the content from the S3 URL
        const { data } = await axios.get(response?.data?.url, {
          responseType: 'arraybuffer',
        });
        const decoded = decodeUnzip(data);
        dispatch(updateLanguagePack(decoded));

        // return data; // Return the fetched language content
      }
      return response?.data?.url;
    },
    onError: (error: any) => {
      const errorCode =
        error?.response?.status || error?.code || 'Unknown Error';
      customNotifications('Language pack', errorCode);
      setIsErrorScript(true);
      //PersistHelper?.clearAllData();
    },
  });

  // Mutation for fetching the task-library script (same GetScript endpoint,
  // different hash). Used by any context that supports the Tasks/Bids tabs.
  const {
    mutateAsync: getTaskScript,
    isPending: isGetTaskScriptPending,
    data: getTaskScriptData,
    isSuccess: successfulGetTaskScript,
  } = useMutation({
    mutationFn: async (taskHash: string) => {
      const response = await Script?.getScript(taskHash);
      return {
        scriptValueGzip: response?.data?.scriptValueGzip,
      };
    },
    onError: (error: any) => {
      const errorCode =
        error?.response?.status || error?.code || 'Unknown Error';
      customNotifications('Task script', errorCode);
    },
  });

  // Effect to trigger the getScript mutation after scriptHashData is available
  useEffect(() => {
    if (scriptHashData?.hash) {
      getScript(scriptHashData.hash); // Trigger the mutation with the script hash
      getScriptLanguagePack(scriptHashData.hash);
    }
  }, [scriptHashData, getScript, getScriptLanguagePack]);

  // Fetch the task-library script for contexts that support Tasks/Bids. Mark
  // settled on success or failure so the main load can proceed either way.
  useEffect(() => {
    if (!supportsTasksBids) {
      setIsTaskScriptSettled(true);
      return;
    }
    // Wait for the script-hash query to resolve before deciding.
    if (isScriptHashDataLoading || isScriptHashDataFetching) {
      return;
    }
    // Task-library hash comes from the GetScriptHashResponse `hashes` map.
    const taskLibraryHash = scriptHashData?.hashes?.TaskLibrary;
    if (!taskLibraryHash) {
      // Persisted load (null payload) or a response with no TaskLibrary entry —
      // nothing to fetch; settle so the main load is not blocked.
      setIsTaskScriptSettled(true);
      return;
    }
    let cancelled = false;
    getTaskScript(taskLibraryHash).finally(() => {
      if (!cancelled) {
        setIsTaskScriptSettled(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    supportsTasksBids,
    getTaskScript,
    scriptHashData,
    isScriptHashDataLoading,
    isScriptHashDataFetching,
  ]);

  // Effect to handle the download after getting the language pack URL
  // useEffect(() => {
  //   if (successfulGetScriptLanguagePack && getScriptLanguagePackData) {
  //     const downloadLanguagePack = async (url: string) => {
  //       const anchor = document.createElement('a');
  //       anchor.href = url;
  //       anchor.target = '_blank';
  //       anchor.download = 'LanguagePack.zip'; // You can set a custom filename if needed
  //       anchor.click();
  //       anchor.remove();
  //       console.log("inside download:", url, anchor)
  //     };

  //     // Trigger download
  //     downloadLanguagePack(getScriptLanguagePackData);
  //   }
  // }, [successfulGetScriptLanguagePack, getScriptLanguagePackData]);

  return {
    scriptHashData,
    isScriptHashDataFetching,
    isScriptHashDataLoading,
    refetch,
    scriptHashDataError,
    getScriptData, // Return mutation data for use
    isGetScriptPending,
    getScriptError,
    successfulGetScript,

    getScriptLanguagePack,
    getScriptLanguagePackData,
    getScriptLanguagePackError,
    isGetScriptLanguagePackPending,
    successfulGetScriptLanguagePack,

    getTaskScriptData,
    isGetTaskScriptPending,
    successfulGetTaskScript,
    isTaskScriptSettled,

    isErrorScript,
  };
};

export default useGetScriptHash;
